import React, { useState, useEffect } from 'react';
import { Handshake } from 'lucide-react';
import { t, getCurrentLanguage } from '../utils/translations';
import api from './api';
import { CardLoader } from './Loader';

const NosPartenaires = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage());

  // Écouter les changements de langue
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLanguage(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, []);

  // Récupérer les partenaires depuis l'API
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/partners/');
        setPartners(response.data);
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement des partenaires:', err);
        setError(t('partners.loadingError'));
        setPartners([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  if (loading) {
    return (
      <section className="py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <CardLoader />
        </div>
      </section>
    );
  }

  if (error || partners.length === 0) {
    return null; // Ne pas afficher la section s'il n'y a pas de partenaires
  }

  // Dupliquer les partenaires pour créer l'effet de boucle infinie
  const duplicatedPartners = [...partners, ...partners, ...partners];

  return (
    <section className="py-10 bg-gradient-to-b from-gray-50 to-white border-t border-gray-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {t('partners.title')}
          </h2>
          <p className="text-gray-600 text-sm mb-3">{t('partners.subtitle')}</p>
          <div className="w-20 h-1 bg-gradient-to-r from-[#089556] to-[#07D1EB] mx-auto rounded-full"></div>
        </div>
      </div>

      {/* Scrolling Container - Full Width */}
      <div className="relative">
        {/* Gradient Overlays for smooth edges */}
        <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none"></div>
        <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none"></div>

        {/* Scrolling Track */}
        <div className="flex overflow-hidden group/track">
          <div 
            className="flex gap-8 animate-scroll"
            style={{
              animation: 'scroll 30s linear infinite',
            }}
          >
            {duplicatedPartners.map((partner, index) => (
              <div
                key={`${partner.id}-${index}`}
                className="flex-shrink-0 w-40 h-24 bg-white rounded-xl border border-gray-200 hover:border-[#089556] hover:shadow-lg transition-all duration-300 flex items-center justify-center p-4 group/logo"
              >
                {partner.logo_url ? (
                  <img
                    src={partner.logo_url}
                    alt={partner.name}
                    className="max-w-full max-h-full object-contain transition-all duration-300 group-hover/logo:scale-110"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="hidden w-full h-full items-center justify-center"
                  style={{ display: partner.logo_url ? 'none' : 'flex' }}
                >
                  <div className="text-center">
                    <Handshake className="h-8 w-8 text-gray-300 mx-auto mb-1" />
                    <span className="text-xs text-gray-400 font-medium">{partner.name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.33%);
          }
        }
        .animate-scroll {
          will-change: transform;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

export default NosPartenaires;
